import { useDispatch, useSelector } from "react-redux";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { userLogin } from "../../redux/authStore/action";
import { Loader } from "../../components/Loader";
import { API } from "../../axios/instance";
import { logout, setActiveBusiness, addBusiness } from "../../redux/authStore/slice";

const createEmptyBusinessForm = (email) => ({
    businessName: "",
    businessEmail: email || "",
    businessPhone: "",
});

export const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loginStatus, loginError } = useSelector((state) => state.auth);

    const [formData, setFormData] = useState({
        userEmail: "",
        password: "",
    });
    const [flowStep, setFlowStep] = useState("credentials"); // credentials | select | create
    const [businessOptions, setBusinessOptions] = useState([]);
    const [selectedBusinessId, setSelectedBusinessId] = useState("");
    const [businessForm, setBusinessForm] = useState(createEmptyBusinessForm(""));
    const [businessError, setBusinessError] = useState("");
    const [creatingBusiness, setCreatingBusiness] = useState(false);
    const [loggedInUser, setLoggedInUser] = useState(null);

    const hasLoginError = useMemo(() => !!loginError, [loginError]);

    const resetFlow = () => {
        setFlowStep("credentials");
        setBusinessOptions([]);
        setSelectedBusinessId("");
        setBusinessForm(createEmptyBusinessForm(""));
        setBusinessError("");
        setCreatingBusiness(false);
        setLoggedInUser(null);
    };

    const handleChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleLogin = (e) => {
        e.preventDefault();
        if (loginStatus === "loading") return;
        setBusinessError("");

        dispatch(userLogin(formData))
            .unwrap()
            .then((payload) => {
                setLoggedInUser(payload.user);
                const businesses = payload?.businesses || [];
                setBusinessOptions(businesses);
                if (businesses.length > 0) {
                    setSelectedBusinessId(businesses[0]?._id || "");
                    setFlowStep("select");
                } else {
                    setBusinessForm(createEmptyBusinessForm(payload?.user?.userEmail));
                    setFlowStep("create");
                }
            })
            .catch((error) => {
                console.error("Login failed:", error);
            });
    };

    const handleBusinessSelection = (e) => {
        setSelectedBusinessId(e.target.value);
        setBusinessError("");
    };

    const handleBusinessContinue = () => {
        if (!selectedBusinessId) {
            setBusinessError("Please select a business to continue.");
            return;
        }

        const selectedBusiness = businessOptions.find((biz) => biz._id === selectedBusinessId);
        if (!selectedBusiness) {
            setBusinessError("Selected business could not be found.");
            return;
        }

        dispatch(setActiveBusiness(selectedBusiness));
        navigate("/dashboard");
    };

    const handleBusinessFormChange = (e) => {
        const { name, value } = e.target;
        setBusinessForm((prev) => ({
            ...prev,
            [name]: value,
        }));
        setBusinessError("");
    };

    const handleCreateBusiness = async (e) => {
        e.preventDefault();
        setBusinessError("");

        if (!businessForm.businessName || !businessForm.businessEmail || !businessForm.businessPhone) {
            setBusinessError("Please fill in business name, email, and phone.");
            return;
        }

        if (!loggedInUser?.id) {
            setBusinessError("Unable to determine the logged-in user. Please try again.");
            return;
        }

        setCreatingBusiness(true);
        try {
            const response = await API.post("business/business/create-business", {
                ...businessForm,
                adminId: loggedInUser.id,
            });
            const createdBusiness = response?.data?.result;
            if (!createdBusiness?._id) {
                throw new Error("Business creation failed. Please try again.");
            }

            setBusinessOptions((prev) => [...prev, createdBusiness]);
            dispatch(addBusiness(createdBusiness));
            dispatch(setActiveBusiness(createdBusiness));
            navigate("/dashboard");
        } catch (error) {
            const message = error?.response?.data?.message || error.message || "Unable to create business.";
            setBusinessError(message);
        } finally {
            setCreatingBusiness(false);
        }
    };

    const handleBackToLogin = () => {
        dispatch(logout());
        resetFlow();
        setFormData({
            userEmail: "",
            password: "",
        });
    };

    const renderCredentialsForm = () => (
        <>
            <div className="text-center mb-3">
                <h1 className="h3 mb-1">Welcome back</h1>
                <p className="text-muted mb-0">Log in to continue</p>
            </div>

            {hasLoginError ? (
                <div className="alert alert-danger" role="alert">
                    {typeof loginError === "string" ? loginError : "Login failed. Please try again."}
                </div>
            ) : null}

            <form onSubmit={handleLogin}>
                <div className="mb-3">
                    <label htmlFor="email" className="form-label">Email address</label>
                    <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="userEmail"
                        value={formData.userEmail}
                        onChange={handleChange}
                        placeholder="Enter email"
                        required
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="password" className="form-label">Password</label>
                    <input
                        type="password"
                        className="form-control"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Password"
                        required
                    />
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="form-check m-0">
                        <input type="checkbox" className="form-check-input" id="rememberMe" />
                        <label className="form-check-label" htmlFor="rememberMe">Remember me</label>
                    </div>
                    <button type="button" className="btn btn-link link-secondary p-0" onClick={(event) => event.preventDefault()}>
                      Forgot password?
                    </button>
                </div>

                <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={loginStatus === "loading"}
                >
                    {loginStatus === "loading" ? <Loader /> : "Log In"}
                </button>
            </form>
        </>
    );

    const renderBusinessSelection = () => (
        <>
            <div className="text-center mb-3">
                <h1 className="h4 mb-1">Select a business</h1>
                <p className="text-muted mb-0">Choose the business you want to manage.</p>
            </div>

            {businessError ? (
                <div className="alert alert-warning" role="alert">
                    {businessError}
                </div>
            ) : null}

            <div className="mb-4">
                {businessOptions.map((business) => (
                    <label key={business._id} className="form-check mb-2">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="businessSelection"
                            value={business._id}
                            checked={selectedBusinessId === business._id}
                            onChange={handleBusinessSelection}
                        />
                        <span className="form-check-label">
                            <span className="fw-semibold d-block">{business.businessName}</span>
                            <small className="text-muted">{business.businessEmail}</small>
                        </span>
                    </label>
                ))}
            </div>

            <div className="d-grid gap-2">
                <button className="btn btn-primary" onClick={handleBusinessContinue}>
                    Continue
                </button>
                <button className="btn btn-outline-secondary" onClick={handleBackToLogin}>
                    Back to login
                </button>
            </div>
        </>
    );

    const renderBusinessCreation = () => (
        <>
            <div className="text-center mb-3">
                <h1 className="h4 mb-1">Create your business</h1>
                <p className="text-muted mb-0">We couldn't find an existing business. Create one to continue.</p>
            </div>

            {businessError ? (
                <div className="alert alert-warning" role="alert">
                    {businessError}
                </div>
            ) : null}

            <form onSubmit={handleCreateBusiness}>
                <div className="mb-3">
                    <label htmlFor="businessName" className="form-label">Business name</label>
                    <input
                        type="text"
                        className="form-control"
                        id="businessName"
                        name="businessName"
                        value={businessForm.businessName}
                        onChange={handleBusinessFormChange}
                        placeholder="Enter business name"
                        required
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="businessEmail" className="form-label">Business email</label>
                    <input
                        type="email"
                        className="form-control"
                        id="businessEmail"
                        name="businessEmail"
                        value={businessForm.businessEmail}
                        onChange={handleBusinessFormChange}
                        placeholder="Enter business email"
                        required
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="businessPhone" className="form-label">Business phone</label>
                    <input
                        type="tel"
                        className="form-control"
                        id="businessPhone"
                        name="businessPhone"
                        value={businessForm.businessPhone}
                        onChange={handleBusinessFormChange}
                        placeholder="Enter phone number"
                        required
                    />
                </div>

                <div className="d-grid gap-2">
                    <button type="submit" className="btn btn-primary" disabled={creatingBusiness}>
                        {creatingBusiness ? <Loader /> : "Create business"}
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={handleBackToLogin} disabled={creatingBusiness}>
                        Back to login
                    </button>
                </div>
            </form>
        </>
    );

    const contentByStep = {
        credentials: renderCredentialsForm(),
        select: renderBusinessSelection(),
        create: renderBusinessCreation(),
    };

    return (
        <div className="container min-vh-100 d-flex align-items-center justify-content-center bg-light">
            <div className="card shadow-sm" style={{ maxWidth: 480, width: "100%" }}>
                <div className="card-body p-4 p-md-5">
                    {contentByStep[flowStep]}
                </div>
            </div>
        </div>
    );
};
